/*
 * camera-reader/hd-voice.js — the server HD voice lane for the camera reader.
 *
 * Why a second lane at all. The device voice (Web Speech) is instant, offline
 * and gives word-by-word highlight, and it stays the default for exactly those
 * reasons. What it is not is pleasant to listen to in Russian — and this tool's
 * whole point is that somebody LISTENS to a page they cannot read. Our own
 * Silero daemon (MIT, v5_cis_base, on the Beget box) speaks native Russian and
 * costs us ~nothing: measured on live prod from this very page, 54 characters
 * came back as 3.74 s of audio in 407 ms.
 *
 * What the numbers forced, so nobody re-derives it:
 *
 *  · ONE REQUEST PER TEXT, not one per sentence. Per-sentence synthesis would
 *    give honest sentence-by-sentence highlight, and it is impossible: the free
 *    rate ceiling is FIVE requests per minute (measured — request #6 returned
 *    429 {scope:"minute", cap:5, retry_after:56}), while an average recognised
 *    page is 907 characters ≈ 9 sentences. It would stall for a minute mid-read.
 *  · SO THERE IS NO WORD HIGHLIGHT ON THIS LANE. The endpoint returns audio
 *    bytes and no timings, and estimating them from character counts would drift
 *    against the speech — on a reading aid a highlight that lies is worse than
 *    no highlight. The UI says which lane gives what; the choice is informed.
 *  · CHUNKING IS STILL NEEDED: `max_chars` is 2000 and 133 of 1327 results
 *    (10 %) are longer. Chunks split on sentence boundaries, play back to back,
 *    and the chunk being read IS highlighted — that boundary is real.
 *
 * Failure is never silence. A blind user pressing "read aloud" must hear
 * something, so every refusal (429 minute / 429 daily pot / 503 busy / offline /
 * daemon down) is classified and handed back to read-aloud.js, which finishes
 * the passage on the device voice and says once, quietly, why the voice changed.
 *
 * Public: window.CRHd = { available, voices, speak, pause, resume, stop,
 *                         isActive, previewUrl, lastQuota }
 */
(function () {
  'use strict';

  var CR = window.__CR || {};
  var CATALOGUE = CR.hdVoices || {};     // { ru: [{id,label,gender,engine,preview}], … }
  var OPEN = !!CR.hdOpen;                // server_tts_open_here() at render time

  var audioEl = null;                    // one element, reused: see __audioGate
  var chunks = [];                       // [{text, blob, url}]
  var at = 0;
  var playing = false, paused = false;
  var cbs = {};
  var quota = null;                      // {used, cap} from the last response
  var abort = null;

  function ae(event, payload) {
    try { window.__ae && window.__ae(event, payload); } catch (e) {}
  }

  function available(langPrefix) {
    return OPEN && !!(CATALOGUE[langPrefix] && CATALOGUE[langPrefix].length);
  }

  function voices(langPrefix) {
    return (CATALOGUE[langPrefix] || []).slice();
  }

  function previewUrl(voiceId) {
    for (var lang in CATALOGUE) {
      if (!Object.prototype.hasOwnProperty.call(CATALOGUE, lang)) continue;
      for (var i = 0; i < CATALOGUE[lang].length; i++) {
        if (CATALOGUE[lang][i].id === voiceId) return CATALOGUE[lang][i].preview || null;
      }
    }
    return null;
  }

  /** Which language bucket a voice id belongs to — the server picks both the
   *  daemon and the text normaliser from it, so it must be the voice's own
   *  language, not the page locale. */
  function langOf(voiceId) {
    for (var lang in CATALOGUE) {
      if (!Object.prototype.hasOwnProperty.call(CATALOGUE, lang)) continue;
      for (var i = 0; i < CATALOGUE[lang].length; i++) {
        if (CATALOGUE[lang][i].id === voiceId) return lang;
      }
    }
    return null;
  }

  var MAX_CHARS = 1900;   // server says 2000; leave room for normalisation growth

  /**
   * The FIRST request is deliberately small. Synthesis is roughly linear above a
   * fixed ~3 s floor, measured on live prod with Silero ru_oksana:
   *
   *      200 chars → 3.6 s   (16.5 s of audio)
   *      350 chars → 3.4 s   (28.5 s of audio)
   *      600 chars → 6.7 s   (48.6 s of audio)
   *      900 chars → 8.8 s   (72.2 s of audio)
   *
   * An average recognised page is 907 characters, so asking for all of it means
   * nine seconds of nothing after pressing "read aloud" — and the person who
   * pressed it cannot see a spinner. Asking for ~350 first cuts that to about
   * three, and those 28 seconds of speech are far more than the ~6 s the rest
   * needs to arrive. Below 350 there is nothing left to win: the floor dominates.
   */
  var FIRST_CHARS = 350;

  /**
   * Split on sentence ends, never mid-word. A sentence longer than the ceiling
   * on its own (OCR of a page with no punctuation is a real input here) is cut
   * at the last space that fits rather than dropped.
   */
  function splitForRequest(text) {
    // ⚠️ No regex lookbehind here — and not in a comment either, which is why
    // this one names it instead of quoting it (NoRegexLookbehindTest reads the
    // file, not the syntax tree). Old WebKit, iOS before 16.4, where Safari,
    // Chrome and Yandex all sit on one engine, fails to PARSE that construct and
    // takes the whole file down with it: the lane would simply not exist on 22 %
    // of this tool's devices. Same sentinel trick read-aloud.js uses next door.
    var parts = String(text || '').replace(/([.!?…。！？])\s+/g, '$1\u0001').split('\u0001');
    var out = [], cur = '';
    function flushLong(s) {
      while (s.length > MAX_CHARS) {
        var cut = s.lastIndexOf(' ', MAX_CHARS);
        if (cut < MAX_CHARS * 0.5) cut = MAX_CHARS;
        out.push(s.slice(0, cut).trim());
        s = s.slice(cut);
      }
      return s;
    }
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      // The ceiling is smaller while nothing has been emitted yet — that first
      // piece is the one the person is waiting on.
      var ceiling = out.length === 0 ? FIRST_CHARS : MAX_CHARS;
      if (p.length > MAX_CHARS) {
        if (cur.trim()) { out.push(cur.trim()); cur = ''; }
        cur = flushLong(p);
        continue;
      }
      if (cur && (cur + ' ' + p).trim().length > ceiling) { out.push(cur.trim()); cur = p; }
      else cur = cur ? cur + ' ' + p : p;
    }
    if (cur.trim()) out.push(cur.trim());
    return out.filter(Boolean);
  }

  function csrf() {
    var m = document.querySelector('meta[name="csrf-token"]');
    return m ? m.getAttribute('content') : '';
  }

  /** An error the caller can act on: `.kind` decides what the person is told. */
  function fail(kind, message, extra) {
    var e = new Error(message || kind);
    e.kind = kind;
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) e[k] = extra[k];
    return e;
  }

  async function synthesize(text, voiceId, ratePct, signal) {
    var lang = langOf(voiceId);
    if (!lang) throw fail('unknown_voice', 'voice not in catalogue');

    var resp;
    try {
      resp = await fetch('/api/tts/synthesize', {
        method: 'POST',
        credentials: 'same-origin',
        signal: signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json, audio/wav',
          'X-CSRF-TOKEN': csrf(),
        },
        body: JSON.stringify({
          text: text, language: lang, voice: voiceId,
          format: 'mp3', rate: ratePct, pitch: 0,
        }),
      });
    } catch (e) {
      if (e && e.name === 'AbortError') throw e;
      throw fail('offline', String((e && e.message) || e));
    }

    // A followed redirect is a PAGE, not audio — the same trap `__serverFile`
    // exists for: fetch reports ok:true and .blob() hands back HTML, which we
    // would happily feed to an <audio> element and then report as speech.
    if (resp.redirected) throw fail('refused', 'redirected to ' + String(resp.url || '').slice(0, 120));

    var cap = resp.headers.get('x-tts-quota-cap');
    var used = resp.headers.get('x-tts-quota-used');
    if (cap != null && used != null) quota = { cap: +cap, used: +used };

    if (resp.status === 429) {
      var body = await resp.json().catch(function () { return {}; });
      if (body.error === 'daily_quota') throw fail('daily_quota', 'daily pot empty', { cap: body.cap });
      throw fail('rate_limited', 'too many per minute', { retryAfter: body.retry_after || 60 });
    }
    if (resp.status === 503) throw fail('busy', 'daemon busy');
    if (resp.status === 403) throw fail('not_allowed', 'lane closed for this account');
    if (!resp.ok) throw fail('refused', 'HTTP ' + resp.status);

    var blob = await resp.blob();
    if (!blob || blob.size < 512 || !/^audio\//.test(blob.type || '')) {
      throw fail('refused', 'not audio: ' + (blob && blob.type) + ' ' + (blob && blob.size) + 'b');
    }
    return blob;
  }

  function ensureEl() {
    if (audioEl) return audioEl;
    if (window.__audioGate && window.__audioGate.element) {
      audioEl = window.__audioGate.element();
    }
    if (!audioEl) {
      audioEl = document.createElement('audio');
      audioEl.preload = 'auto';
      document.body.appendChild(audioEl);
    }
    return audioEl;
  }

  function revoke() {
    for (var i = 0; i < chunks.length; i++) {
      if (chunks[i].url) { try { URL.revokeObjectURL(chunks[i].url); } catch (e) {} chunks[i].url = null; }
    }
  }

  var expected = 0;   // how many chunks this passage HAS, not how many arrived

  async function playFrom(i) {
    var el = ensureEl();
    at = i;
    if (at >= expected) { finish(); return; }

    // The next chunk may still be in flight: chunk 0 starts playing before the
    // rest are fetched, on purpose. Without this wait a short first chunk ends
    // before its successor lands, `at >= chunks.length` reads as "passage over",
    // and the rest of the page is silently never spoken — the kind of truncation
    // nobody reports because it looks like the text simply finished.
    var waited = 0;
    while (playing && at >= chunks.length && waited < 600) {
      await new Promise(function (r) { setTimeout(r, 100); });
      waited++;
    }
    if (!playing) return;
    if (at >= chunks.length) { finish(); return; }   // the fetch failed; onError already said so

    var c = chunks[at];
    if (!c.url) c.url = URL.createObjectURL(c.blob);
    if (cbs.onChunk) { try { cbs.onChunk(at, chunks.length); } catch (e) {} }

    el.onended = function () {
      if (!playing) return;
      playFrom(at + 1);
    };
    el.onerror = function () {
      if (!playing) return;
      stop();
      if (cbs.onError) cbs.onError(fail('playback', 'audio element error'));
    };

    try {
      // Through the gate: this play() follows an awaited fetch, and Safari and
      // Chrome-on-Android refuse a bare play() there — the click's activation
      // died with the handler's task.
      if (window.__audioGate && window.__audioGate.play) await window.__audioGate.play(el, c.url);
      else { el.src = c.url; await el.play(); }
    } catch (e) {
      stop();
      if (cbs.onError) cbs.onError(fail('blocked', String((e && e.name) || e)));
    }
  }

  function finish() {
    playing = false; paused = false;
    revoke();
    if (cbs.onEnd) { try { cbs.onEnd(); } catch (e) {} }
  }

  /**
   * Speak the whole passage. Resolves when playback has STARTED (so the caller
   * can flip its button); completion arrives through opts.onEnd.
   */
  async function speak(text, voiceId, opts) {
    opts = opts || {};
    cbs = { onEnd: opts.onEnd, onError: opts.onError, onChunk: opts.onChunk };
    stop();

    var pieces = splitForRequest(text);
    if (!pieces.length) throw fail('empty', 'nothing to read');
    expected = pieces.length;

    // Rate as the server wants it: whole percent, -50…+100. The slider is a
    // multiplier, so 1.0× is 0 % and 2.0× is +100 %.
    var ratePct = Math.max(-50, Math.min(100, Math.round(((opts.rate || 1) - 1) * 100)));

    abort = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var signal = abort ? abort.signal : undefined;

    var t0 = Date.now();
    var first = await synthesize(pieces[0], voiceId, ratePct, signal);
    chunks = [{ text: pieces[0], blob: first, url: null }];
    playing = true; paused = false;
    ae('feature_use', {
      slug: 'camera-reader', f: 'hd_voice_start', voice: voiceId,
      chunks: pieces.length, chars: text.length, ms: Date.now() - t0,
    });

    await playFrom(0);

    // The rest are fetched while the first one plays. Sequential on purpose:
    // five requests a minute is the ceiling, and a burst would spend it.
    for (var i = 1; i < pieces.length && playing; i++) {
      try {
        var b = await synthesize(pieces[i], voiceId, ratePct, signal);
        chunks.push({ text: pieces[i], blob: b, url: null });
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        // Part of the passage is already speaking; the rest cannot be fetched.
        // Hand the REMAINDER back so the caller finishes it on the device voice
        // instead of stopping in the middle of a page.
        if (cbs.onError) cbs.onError(fail(e.kind || 'refused', e.message, { remainder: pieces.slice(i).join(' ') }));
        return;
      }
    }
  }

  function pause() {
    if (!playing) return;
    paused = true; playing = false;
    try { audioEl && audioEl.pause(); } catch (e) {}
  }

  function resume() {
    if (!paused) return;
    paused = false; playing = true;
    try { audioEl && audioEl.play(); } catch (e) {}
  }

  function stop() {
    playing = false; paused = false;
    if (abort) { try { abort.abort(); } catch (e) {} abort = null; }
    if (audioEl) {
      try { audioEl.pause(); } catch (e) {}
      audioEl.onended = null;
      audioEl.onerror = null;
    }
    revoke();
    chunks = [];
    at = 0;
    expected = 0;
  }

  window.CRHd = {
    available: available,
    voices: voices,
    previewUrl: previewUrl,
    speak: speak,
    pause: pause,
    resume: resume,
    stop: stop,
    isActive: function () { return playing || paused; },
    lastQuota: function () { return quota; },
  };
})();
